import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/rn3";
import { useLang } from "@/lib/i18n";

const AuthContext = createContext(null);

// ---- Session management constants ----
const SESSION_BROADCAST_CHANNEL = "rn3_auth_sync";
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes of no activity
const INACTIVITY_WARNING_MS = 30 * 1000;     // show 30-second warning before logout
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
export const LAST_ACTIVE_STORAGE_KEY = "rn3_last_active";

// On login, reconcile the user's saved language with their CURRENT local choice.
// Rule: the local toggle ALWAYS wins — if the visitor explicitly picked ES on the marketing
// site (or any post-login toggle), we honor that and push it up to the server. We only adopt
// the server-stored preferred_language when the browser has no saved choice yet.
const LanguageSync = () => {
  const { user } = useContext(AuthContext);
  const { lang, setLang } = useLang();
  useEffect(() => {
    if (!user?.id) return;
    let localExplicit = null;
    try { localExplicit = localStorage.getItem("rn3.lang"); } catch { /* noop */ }
    if (localExplicit) {
      // Browser had an explicit choice → push it to the server if it differs.
      if (user.preferred_language !== lang) {
        api.patch("/users/me", { preferred_language: lang }).catch(() => { /* noop */ });
      }
      return;
    }
    // No explicit local choice → adopt the server's stored preference (first login on this device).
    if (user.preferred_language && user.preferred_language !== lang) {
      setLang(user.preferred_language);
    }
  }, [user?.id, lang, setLang]);
  return null;
};

// SessionGuard: handles 5-minute inactivity auto-logout with 30s warning popup.
// Only runs when a user is signed in. Listens to mouse/keyboard/touch/scroll events.
const SessionGuard = () => {
  const { user, logout } = useContext(AuthContext);
  const { lang } = useLang();
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const warningToastIdRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;

    const tr = (es, en) => (lang === "es" ? es : en);

    const clearAllTimers = () => {
      if (inactivityTimerRef.current) { clearTimeout(inactivityTimerRef.current); inactivityTimerRef.current = null; }
      if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null; }
      if (warningToastIdRef.current !== null) {
        try { toast.dismiss(warningToastIdRef.current); } catch { /* noop */ }
        warningToastIdRef.current = null;
      }
    };

    const performInactivityLogout = async () => {
      clearAllTimers();
      try { await logout(); } catch { /* noop */ }
      toast.error(tr("Has sido desconectado por inactividad.", "You've been signed out due to inactivity."));
      // Send user back to the portal sign-in screen
      try { window.location.assign("/portal"); } catch { /* noop */ }
    };

    const showWarning = () => {
      // Show warning toast 30 seconds before auto-logout
      try {
        warningToastIdRef.current = toast.warning(
          tr("Tu sesión está por expirar", "Session expiring soon"),
          {
            description: tr(
              "Serás desconectado en 30 segundos por inactividad.",
              "You'll be signed out in 30 seconds due to inactivity."
            ),
            duration: INACTIVITY_WARNING_MS,
            action: {
              label: tr("Mantener sesión", "Stay signed in"),
              onClick: () => { resetTimers(); },
            },
          }
        );
      } catch { /* noop */ }
      warningTimerRef.current = setTimeout(performInactivityLogout, INACTIVITY_WARNING_MS);
    };

    const resetTimers = () => {
      clearAllTimers();
      inactivityTimerRef.current = setTimeout(showWarning, INACTIVITY_TIMEOUT_MS - INACTIVITY_WARNING_MS);
    };

    // Throttle activity handler so we don't spam clearTimeout/setTimeout on every mousemove
    let lastActivityAt = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityAt < 1000) return; // throttle to once per second
      lastActivityAt = now;
      // Persist a coarse "last active" timestamp for the Portal UI. Throttled
      // separately to once a minute to avoid spamming localStorage.
      try {
        const lastPersisted = parseInt(localStorage.getItem(LAST_ACTIVE_STORAGE_KEY) || "0", 10);
        if (!lastPersisted || now - lastPersisted >= 60 * 1000) {
          localStorage.setItem(LAST_ACTIVE_STORAGE_KEY, String(now));
        }
      } catch { /* noop */ }
      // If the warning is already visible, ignore passive activity — user must explicitly
      // click "Stay signed in" to extend the session.
      if (warningToastIdRef.current !== null) return;
      resetTimers();
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    // Initialize the persisted "last active" stamp so the UI has something to show
    // immediately after sign-in (before the user moves the mouse).
    try {
      if (!localStorage.getItem(LAST_ACTIVE_STORAGE_KEY)) {
        localStorage.setItem(LAST_ACTIVE_STORAGE_KEY, String(Date.now()));
      }
    } catch { /* noop */ }
    resetTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      clearAllTimers();
    };
  }, [user?.id, lang, logout]);

  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const broadcastChannelRef = useRef(null);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem("rn3_token");
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("rn3_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ---- Cross-tab logout sync ----
  // If the user signs out in one tab, every other open tab should sign out too.
  // We use BroadcastChannel as the primary transport and `storage` events as a fallback.
  useEffect(() => {
    const handleRemoteLogout = () => {
      try { localStorage.removeItem("rn3_token"); } catch { /* noop */ }
      try { localStorage.removeItem(LAST_ACTIVE_STORAGE_KEY); } catch { /* noop */ }
      setUser((prev) => {
        if (!prev) return prev; // already signed out — don't toast
        const lang = (() => { try { return localStorage.getItem("rn3.lang"); } catch { return null; } })();
        const msg = lang === "es" ? "Has cerrado sesión en otra pestaña." : "You signed out in another tab.";
        try { toast.info(msg); } catch { /* noop */ }
        return null;
      });
    };

    // BroadcastChannel (modern browsers)
    let channel = null;
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      try {
        channel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
        channel.onmessage = (e) => {
          if (e?.data === "logout") handleRemoteLogout();
        };
        broadcastChannelRef.current = channel;
      } catch { /* noop */ }
    }

    // localStorage `storage` event fallback (fires in OTHER tabs only)
    const onStorage = (e) => {
      if (e.key === "rn3_token" && e.newValue === null && e.oldValue) {
        handleRemoteLogout();
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      if (channel) { try { channel.close(); } catch { /* noop */ } }
      broadcastChannelRef.current = null;
    };
  }, []);

  const broadcastLogout = () => {
    try {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage("logout");
      } else if (typeof window !== "undefined" && "BroadcastChannel" in window) {
        const c = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
        c.postMessage("logout");
        c.close();
      }
    } catch { /* noop */ }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // 2FA gate: when admin (always) or client (opted-in), the server returns a challenge instead of a token.
    if (data?.requires_2fa) {
      return { requires_2fa: true, challenge_token: data.challenge_token, channel: data.channel, destination_masked: data.destination_masked, expires_at: data.expires_at, user_email: data.user_email };
    }
    localStorage.setItem("rn3_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const verify2fa = async (challenge_token, code) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge_token, code });
    localStorage.setItem("rn3_token", data.token);
    setUser(data.user);
    return data;
  };

  const resend2fa = async (challenge_token) => {
    const { data } = await api.post("/auth/2fa/resend", { challenge_token });
    return data;
  };

  const useRecoveryCode = async (challenge_token, recovery_code) => {
    const { data } = await api.post("/auth/2fa/recovery", { challenge_token, recovery_code });
    localStorage.setItem("rn3_token", data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ok */ }
    localStorage.removeItem("rn3_token");
    try { localStorage.removeItem(LAST_ACTIVE_STORAGE_KEY); } catch { /* noop */ }
    setUser(null);
    // Notify every other open tab to sign out too.
    broadcastLogout();
  };

  // Revoke every existing JWT for this user (every device, every browser).
  // Server bumps users.session_epoch; old tokens then fail the sv check in current_user.
  const signOutEverywhere = async () => {
    try { await api.post("/auth/sign-out-everywhere"); } catch { /* ok — still log out locally */ }
    localStorage.removeItem("rn3_token");
    try { localStorage.removeItem(LAST_ACTIVE_STORAGE_KEY); } catch { /* noop */ }
    setUser(null);
    broadcastLogout();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verify2fa, resend2fa, useRecoveryCode, logout, signOutEverywhere, refresh }}>
      <LanguageSync />
      <SessionGuard />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
